// Curated guest testimonials shown inside each room article on the
// hotel detail page. Quotes are room-type flavoured (double, twin,
// family, junior, royal suite, standard, deluxe, bivouac tent) and
// selected deterministically from the room name so the same room
// always shows the same testimonial across renders (no flicker,
// safe for CMS diffs). Localised in EN/FR/ES.

const ROOM_TESTIMONIALS = {
  double: {
    quote: {
      en: "A queen bed under a vaulted ceiling, warm wool blankets, and the smell of cedar at dusk. We slept like the kasbah wanted us to.",
      fr: "Un lit queen sous plafond voûté, des couvertures de laine chaudes et l'odeur du cèdre au crépuscule. Nous avons dormi comme la kasbah le voulait.",
      es: "Cama queen bajo techo abovedado, mantas de lana cálidas y aroma a cedro al anochecer. Dormimos como la kasbah quería.",
    },
    author: "Emma R.",
    origin: { en: "Lyon", fr: "Lyon", es: "Lyon" },
    avatar:
      "https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&w=96&h=96&q=80",
  },
  twin: {
    quote: {
      en: "Two beds, one balcony over the cactus garden — perfect for us as sisters. Small details everywhere: fossil marble in the bath, hand-stitched pillows.",
      fr: "Deux lits, un balcon sur le jardin de cactus — parfait entre sœurs. Détails partout : marbre fossile, coussins brodés main.",
      es: "Dos camas, un balcón sobre el jardín de cactus — perfecto entre hermanas. Detalles por todas partes: mármol fósil, cojines bordados a mano.",
    },
    author: "Ana & Lucía M.",
    origin: { en: "Madrid", fr: "Madrid", es: "Madrid" },
    avatar:
      "https://images.unsplash.com/photo-1531123897727-8f129e1688ce?auto=format&fit=crop&w=96&h=96&q=80",
  },
  family: {
    quote: {
      en: "The connecting sleeping nooks meant our kids had their own space without ever being far. Bathtub big enough for two, spotless throughout.",
      fr: "Les alcôves communicantes ont permis aux enfants d'avoir leur espace sans être loin. Baignoire pour deux, tout d'une propreté irréprochable.",
      es: "Los espacios comunicantes permitieron a los niños tener su rincón sin estar lejos. Bañera para dos, todo impecable.",
    },
    author: "The Bertrands",
    origin: { en: "Brussels", fr: "Bruxelles", es: "Bruselas" },
    avatar:
      "https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&w=96&h=96&q=80",
  },
  junior: {
    quote: {
      en: "Living area, private terrace, king bed you sink into. We opened the doors at sunrise and had breakfast overlooking the dunes.",
      fr: "Salon, terrasse privée, lit king dans lequel on s'enfonce. Nous avons ouvert les portes au lever du soleil et pris le petit-déjeuner face aux dunes.",
      es: "Salón, terraza privada, cama king en la que te hundes. Abrimos las puertas al amanecer y desayunamos frente a las dunas.",
    },
    author: "David C.",
    origin: { en: "New York", fr: "New York", es: "Nueva York" },
    avatar:
      "https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?auto=format&fit=crop&w=96&h=96&q=80",
  },
  royal: {
    quote: {
      en: "The Jacuzzi terrace facing the desert is unforgettable. We spent our anniversary here — worth every dirham. The service reads your mind.",
      fr: "Le jacuzzi en terrasse face au désert reste inoubliable. Nous y avons fêté notre anniversaire — chaque dirham en vaut la peine. Le service devine vos envies.",
      es: "El jacuzzi en la terraza frente al desierto es inolvidable. Celebramos nuestro aniversario aquí — vale cada dírham. El servicio adivina tus deseos.",
    },
    author: "Sarah & James B.",
    origin: { en: "London", fr: "Londres", es: "Londres" },
    avatar:
      "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?auto=format&fit=crop&w=96&h=96&q=80",
  },
  suite: {
    quote: {
      en: "A separate lounge, dining nook, terrace opening onto the pool — a suite that feels like a small riad. We didn't want to leave.",
      fr: "Salon séparé, coin salle à manger, terrasse ouverte sur la piscine — une suite qui a des allures de petit riad. Nous ne voulions plus partir.",
      es: "Salón separado, rincón comedor, terraza sobre la piscina — una suite con aire de pequeño riad. No queríamos irnos.",
    },
    author: "Andrea L.",
    origin: { en: "Milan", fr: "Milan", es: "Milán" },
    avatar:
      "https://images.unsplash.com/photo-1489424731084-a5d8b219a5bb?auto=format&fit=crop&w=96&h=96&q=80",
  },
  standard: {
    quote: {
      en: "Simple but full of soul. Handwoven Berber blankets, a private balcony over the palm grove, and staff who remember your name from day one.",
      fr: "Simple mais plein d'âme. Couvertures berbères tissées main, balcon privé sur la palmeraie, et un personnel qui se souvient de votre prénom dès le premier jour.",
      es: "Sencillo pero lleno de alma. Mantas bereberes tejidas a mano, balcón privado sobre el palmeral y un equipo que recuerda tu nombre desde el primer día.",
    },
    author: "Nadia F.",
    origin: { en: "Amsterdam", fr: "Amsterdam", es: "Ámsterdam" },
    avatar:
      "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=96&h=96&q=80",
  },
  bivouac: {
    quote: {
      en: "Berber tent lit only by lanterns, the milky way overhead, a fire crackling outside. This was the night that changed the trip.",
      fr: "Tente berbère éclairée aux lanternes, la voie lactée au-dessus, un feu qui crépite dehors. La nuit qui a changé le voyage.",
      es: "Jaima bereber iluminada solo con farolillos, la vía láctea sobre nosotros, una hoguera crepitando fuera. Esa noche cambió el viaje.",
    },
    author: "Kenji T.",
    origin: { en: "Kyoto", fr: "Kyoto", es: "Kioto" },
    avatar:
      "https://images.unsplash.com/photo-1517841905240-472988babdf9?auto=format&fit=crop&w=96&h=96&q=80",
  },
  deluxe: {
    quote: {
      en: "The vaulted ceiling and adobe walls hold the day's warmth and cool the night — genuine Berber architecture, quietly refined.",
      fr: "Le plafond voûté et les murs d'adobe gardent la chaleur du jour et rafraîchissent la nuit — vraie architecture berbère, sobrement raffinée.",
      es: "El techo abovedado y los muros de adobe guardan el calor del día y refrescan la noche — arquitectura bereber auténtica, sobria y refinada.",
    },
    author: "Isabela M.",
    origin: { en: "Lisbon", fr: "Lisbonne", es: "Lisboa" },
    avatar:
      "https://images.unsplash.com/photo-1502823403499-6ccfcf4fb453?auto=format&fit=crop&w=96&h=96&q=80",
  },
};

// Fallback rotation when we can't infer a category from the room name.
const ROTATION = [
  "standard",
  "double",
  "family",
  "junior",
  "suite",
  "royal",
  "deluxe",
];

const KEYWORDS = [
  ["royal", ["royal", "signature", "flagship"]],
  ["bivouac", ["tent", "camp", "bivouac", "haima", "jaima"]],
  ["suite", ["suite"]],
  ["junior", ["junior", "premium"]],
  ["family", ["family", "familiar", "famille"]],
  ["twin", ["twin"]],
  ["double", ["double", "queen"]],
  ["deluxe", ["deluxe", "luxe", "kasbah"]],
  ["standard", ["standard", "classic"]],
];

const pickTestimonialKey = (roomName, idx = 0) => {
  const s = String(roomName || "").toLowerCase();
  for (const [key, words] of KEYWORDS) {
    if (words.some((w) => s.includes(w))) return key;
  }
  return ROTATION[idx % ROTATION.length];
};

export { pickTestimonialKey };

export const getRoomTestimonial = (roomName, idx = 0) => {
  const key = pickTestimonialKey(roomName, idx);
  return ROOM_TESTIMONIALS[key] || ROOM_TESTIMONIALS.standard;
};

// Rotation helper: given a room name/index, a list of admin overrides
// (fetched from the backend for the current hotel), and an optional
// seed, returns one testimonial to render. Priority: matching admin
// items are picked first (randomly if 2+), else the curated fallback.
// Passing `seed` lets callers pin the choice per mount for stability.
export const pickRoomTestimonial = (
  roomName,
  idx = 0,
  adminItems = [],
  seed = null
) => {
  const key = pickTestimonialKey(roomName, idx);
  const matches = (adminItems || []).filter(
    (it) => (it.room_key || "").toLowerCase() === key
  );
  if (matches.length > 0) {
    const pickIdx =
      typeof seed === "number"
        ? Math.abs(seed) % matches.length
        : Math.floor(Math.random() * matches.length);
    const it = matches[pickIdx];
    // Shape into ExperienceTestimonial format
    return {
      quote: it.quote,
      author: it.author,
      origin: it.origin,
      avatar: it.avatar,
    };
  }
  return ROOM_TESTIMONIALS[key] || ROOM_TESTIMONIALS.standard;
};

export default ROOM_TESTIMONIALS;
